import React, { Fragment, useState } from 'react'
import { PlusCircle as PlusCircleIcon } from 'react-feather'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Alert } from '@material-ui/lab'
import { Box, Button, Typography } from '@material-ui/core'

import { AlertDialog, Dai, Permitted } from 'components'
import { PlanActionForm } from './components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const PlanActionSection = ({
  loading,
  isLoading,
  isUsersLoading,
  setLoading,
  refresh,
  dataProcessTreatment,
  users,
}) => {
  const [openPlanAction, setOpenPlanAction] = useState(false)
  const [loadingDai, setLoadingDai] = useState(false)
  const [selectedEvidencePlanAction, setSelectedEvidencePlanAction] =
    useState(null)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const { STATUS } = constants.dataProcessTreatment
  const { DAI_SOURCE_ID } = constants.planAction
  const { TAGS } = constants.dai.solutions

  const evidencePlanActions = dataProcessTreatment?.evidencePlanActions || []

  const hasDaiGeneratedPlanActions = evidencePlanActions.some(
    (evidencePlanAction) =>
      evidencePlanAction?.planActions?.some(
        (planAction) => planAction?.source === DAI_SOURCE_ID,
      ),
  )

  const isTreated = dataProcessTreatment?.status === STATUS.TREATED_ID
  const isRiskAssumed = dataProcessTreatment?.status === STATUS.RISK_ASSUMED_ID
  const isInactive = dataProcessTreatment?.status === STATUS.INACTIVE_ID

  const isClose = isTreated || isRiskAssumed || isInactive

  const hasDefinedRisk = !isEmpty(
    dataProcessTreatment?.dataProcess?.dataProcessRisk,
  )

  const daiDisabledTooltipTitle = loadingDai
    ? 'Gerando planos de ação'
    : hasDaiGeneratedPlanActions
    ? 'A DAI já gerou planos de ação para este tratamento. Para gerar novamente, exclua os planos existentes'
    : !hasDefinedRisk
    ? 'O processo precisa estar com o risco definido para poder gerar planos de ação com a DAI'
    : ''

  const handleOpenPlanAction = () => setOpenPlanAction(true)
  const handleClosePlanAction = () => setOpenPlanAction(false)

  const handleClearSelectedEvidencePlanAction = () =>
    setSelectedEvidencePlanAction(null)

  const handleGeneratePlanActions = async () => {
    try {
      setLoadingDai(true)

      const response =
        await service.dponet.dataProcessTreatment.generatePlanActions({
          dataProcessTreatmentId: dataProcessTreatment?.id,
        })

      const hasGenerated = !isEmpty(response?.data?.planActions)

      snackbar.open({
        message: hasGenerated
          ? 'Planos de ação gerados com sucesso!'
          : 'A DAI não identificou novos planos de ação a serem gerados',
        variant: hasGenerated ? 'success' : 'info',
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoadingDai(false)
    }
  }

  const handleDeletePlanAction = async () => {
    try {
      setLoading(true)

      await service.dponet.dataProcessTreatment.deleteEvidencePlanAction({
        dataProcessTreatmentId: dataProcessTreatment?.id,
        evidencePlanActionId: selectedEvidencePlanAction,
      })

      snackbar.open({
        message: 'Plano de ação removido com sucesso!',
        variant: 'success',
      })
      refresh()
      handleClearSelectedEvidencePlanAction()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Plano de Ação</Typography>
        <Permitted
          someTags={[
            constants.permissions.DATA_PROCESS_TREATMENT.MANAGE,
            constants.permissions.DATA_PROCESSES.CREATE,
          ]}
        >
          <Box display="flex" gridGap={theme.spacing(2)} alignItems="center">
            <Dai.Permitted
              tag={TAGS.GENERATE_DATA_PROCESS_TREATMENT_PLAN_ACTION}
            >
              <Dai.StarButton
                aiAlert
                onClick={handleGeneratePlanActions}
                disabled={
                  loadingDai ||
                  isLoading ||
                  isClose ||
                  !hasDefinedRisk ||
                  hasDaiGeneratedPlanActions
                }
                disabledTooltipTitle={daiDisabledTooltipTitle}
              >
                Adicionar com a DAI
              </Dai.StarButton>
            </Dai.Permitted>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlusCircleIcon size={20} />}
              onClick={handleOpenPlanAction}
              disabled={isLoading || isClose}
            >
              Adicionar Manualmente
            </Button>
          </Box>
        </Permitted>
      </Box>
      {!isLoading && !loadingDai && isEmpty(evidencePlanActions) && (
        <Alert severity="info" variant="outlined">
          Nenhum plano de ação foi definido. Você pode adicionar um plano de
          ação automaticamente, utilizando nossa IA, clicando em&nbsp;
          <strong>Adicionar com a DAI.</strong>
        </Alert>
      )}
      <Dai.Loading
        texts={constants.dai.questionControls.LOADING_TEXTS}
        loading={loadingDai}
      />
      {openPlanAction && (
        <PlanActionForm
          users={users}
          onClose={handleClosePlanAction}
          isUsersLoading={isUsersLoading}
          refresh={refresh}
          dataProcessTreatmentId={dataProcessTreatment?.id}
        />
      )}
      {!isLoading &&
        evidencePlanActions?.map((evidencePlanAction) =>
          evidencePlanAction?.planActions?.map((planAction) => {
            const isInactivePlanAction =
              planAction?.status === constants.planAction.INACTIVE_STATUS_ID
            const isConcludedPlanAction =
              planAction?.status === constants.planAction.CONCLUSION_STATUS_ID

            const isClosePlanAction =
              isInactivePlanAction || isConcludedPlanAction

            return (
              <PlanActionForm
                key={planAction.id}
                dataProcessTreatmentId={dataProcessTreatment?.id}
                evidencePlanAction={evidencePlanAction}
                planAction={planAction}
                refresh={refresh}
                users={users}
                isUsersLoading={isUsersLoading}
                setSelectedEvidencePlanAction={setSelectedEvidencePlanAction}
                isDisabled={isClose || isClosePlanAction}
              />
            )
          }),
        )}
      {!!selectedEvidencePlanAction && (
        <AlertDialog
          variant="outlined"
          severity="warning"
          content="Tem certeza que deseja remover este plano de ação? Ao removê-lo, todas as informações associadas serão perdidas."
          open={!!selectedEvidencePlanAction}
          onClose={handleClearSelectedEvidencePlanAction}
          loading={loading}
          dialogActions={
            <Fragment>
              <Button
                variant="outlined"
                color="default"
                onClick={handleClearSelectedEvidencePlanAction}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeletePlanAction}
                variant="contained"
                className={classes.warningButton}
                disabled={loading}
              >
                Remover
              </Button>
            </Fragment>
          }
        />
      )}
    </Box>
  )
}

PlanActionSection.propTypes = {
  loading: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isUsersLoading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  dataProcessTreatment: PropTypes.object,
  users: PropTypes.array,
}

export default PlanActionSection
